<template>
    <hts-drawer
        v-model="visible"
        :title="$t('labels.partnerViewDetail')"
        size="large"
        :no-reset="true"
        @close="closeOverSide">
        <div v-if="loading">
            <hts-card :title="$t('labels.generalInformation')">
                <hts-form-input
                    v-model="dataFormEdit.name"
                    :label="$t('labels.companyName')"
                    readonly
                    :color="props.color"
                    name="name"
                    prop="name" />
                <div class="grid gap-4 grid-cols-1 sm:grid-cols-2">
                    <hts-form-input
                        v-model="dataFormEdit.abbreviationName"
                        :label="$t('labels.abbreviationName')"
                        readonly
                        :color="props.color"
                        name="abbreviationName"
                        prop="abbreviationName" />
                    <hts-form-input
                        v-model="dataFormEdit.taxNumber"
                        :label="$t('labels.taxNumber')"
                        :color="props.color"
                        readonly
                        name="taxNumber"
                        prop="taxNumber" />
                    <hts-form-input
                        v-model="dataFormEdit.email"
                        :label="$t('labels.email')"
                        :color="props.color"
                        readonly
                        name="email"
                        prop="email" />
                    <hts-form-phone
                        v-model="dataFormEdit.phone"
                        :label="$t('labels.phone')"
                        required
                        prop="phone"
                        name="phone"
                        readonly
                        @updateCode="(val) => (dataFormEdit.phone.code = val)" />
                    <hts-form-input
                        v-model="dataFormEdit.representation"
                        :label="$t('labels.representation')"
                        :color="props.color"
                        readonly
                        name="representation"
                        prop="representation" />
                    <hts-form-input
                        v-model="dataFormEdit.office"
                        :label="$t('labels.office')"
                        :color="props.color"
                        readonly
                        name="office"
                        prop="office" />
                    <hts-form-input
                        v-model="dataFormEdit.website"
                        :label="$t('labels.website')"
                        :color="props.color"
                        readonly
                        name="website"
                        prop="website" />
                    <hts-form-input
                        v-model="dataFormEdit.fax"
                        :label="$t('labels.fax')"
                        :color="props.color"
                        readonly
                        name="fax"
                        prop="fax" />
                    <hts-form-input
                        v-model="dataFormEdit.address"
                        :label="$t('labels.address')"
                        :color="props.color"
                        readonly
                        name="address"
                        prop="address" />
                    <hts-form-input
                        v-model="dataFormEdit.locationName"
                        :label="$t('labels.location')"
                        :color="props.color"
                        name="location"
                        prop="location"
                        readonly
                        autocomplete="off" />
                </div>
            </hts-card>
            <hts-card :title="$t('labels.typeCustomer')">
                <div>
                    <div class="flex">
                        <hts-form-checkbox
                            v-model="dataFormEdit.type"
                            :label="$t('labels.typeCustomer')"
                            :items="selectCooperatorTypes"
                            readonly
                            prop="typeCustomer"
                            name="typeCustomer" />
                    </div>
                </div>
                <hts-form-button-group
                    v-if="customerTypeTabs && customerTypeTabs.length"
                    v-model="selectCustomerTypeTabs"
                    :items="customerTypeTabs"
                    type="button"
                    class="my-5" />
                <div
                    v-if="dataFormEdit.type.includes('CUSTOMER') && selectCustomerTypeTabs"
                    v-show="selectCustomerTypeTabs === 'CUSTOMER'"
                    class="grid gap-4 grid-cols-1 sm:grid-cols-2">
                    <hts-form-input
                        v-model="dataFormEdit.commission"
                        type="number"
                        readonly
                        :label="$t('labels.commission2')"
                        :color="props.color"
                        name="commission2"
                        prop="commission2" />

                    <div
                        class="grid gap-4"
                        :class="{
                            'grid-cols-1': typeof dataFormEdit.statusConfirmMarkup !== 'number',
                            'grid-cols-1 sm:grid-cols-2': typeof dataFormEdit.statusConfirmMarkup === 'number'
                        }">
                        <hts-form-input
                            v-model="dataFormEdit.markup"
                            :label="$t('labels.markupPercent')"
                            :color="props.color"
                            readonly
                            name="markup"
                            prop="markup" />
                        <div class="flex items-center pt-6">
                            <div
                                v-if="!dataFormEdit.statusMarkup && dataFormEdit.statusConfirmMarkup === 0 && !checkPermission(layoutPermissions.partner_confirm_status_markup)"
                                class="flex">
                                <hi-arrow-path-icon
                                    class="w-5 h-5"
                                    aria-hidden="true" />
                                <span>
                                    {{ $t('labels.pendingApproval') }}
                                </span>
                            </div>
                            <div
                                v-else-if="dataFormEdit.statusMarkup && dataFormEdit.statusConfirmMarkup === 1"
                                class="text-green-400 flex">
                                <hi-check-circle-icon
                                    class="w-5 h-5"
                                    aria-hidden="true" />
                                <span>
                                    {{ $t('labels.approved') }}
                                </span>
                            </div>
                            <div
                                v-else-if="!dataFormEdit.statusMarkup && dataFormEdit.statusConfirmMarkup === 2"
                                class="text-error-400 flex">
                                <hi-x-circle-icon
                                    class="w-5 h-5"
                                    aria-hidden="true" />
                                <span>
                                    {{ $t('labels.reject') }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <hts-form-input
                        v-model="dataFormEdit.curatorName"
                        :label="$t('labels.curator')"
                        readonly
                        name="curator"
                        prop="curator" />

                    <hts-form-input
                        v-model="dataFormEdit.distributionName"
                        :label="$t('labels.abstemious')"
                        readonly
                        name="distribution"
                        prop="abstemious" />
                </div>

                <div
                    v-if="dataFormEdit.type.includes('PARTNER') && selectCustomerTypeTabs"
                    v-show="selectCustomerTypeTabs === 'PARTNER'">
                    <span class="text-base block px-0 font-medium text-default-500">
                        {{ $t('labels.moocSavingPolicy') }}
                    </span>
                    <div class="pl-10">
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <hts-form-input
                                v-model="dataFormEdit.cooperatorPolicy.moocSavingPolicy.freeHourNumber"
                                :label="$t('labels.freeHourNumber')"
                                type="number"
                                readonly
                                :color="props.color"
                                name="keepMoocByHours"
                                prop="keepMoocByHours" />
                            <hts-form-input
                                v-model="dataFormEdit.cooperatorPolicy.moocSavingPolicy.moocKeepHourly"
                                :label="$t('labels.moocKeepHourly')"
                                type="number"
                                readonly
                                :color="props.color"
                                name="moocKeepHourly"
                                prop="moocKeepHourly" />

                            <hts-form-input
                                v-model="dataFormEdit.cooperatorPolicy.moocSavingPolicy.moocKeepOvernight"
                                :label="$t('labels.moocKeepOvernight')"
                                type="number"
                                readonly
                                :color="props.color"
                                name="moocKeepOvernight"
                                prop="moocKeepOvernight" />
                            <hts-form-input
                                v-model="dataFormEdit.cooperatorPolicy.moocSavingPolicy.calculatedByHourNextDay"
                                :label="$t('labels.calculatedByHourNextDay')"
                                type="number"
                                readonly
                                :color="props.color"
                                name="calculatedByHourNextDay"
                                prop="calculatedByHourNextDay" />

                            <hts-form-input
                                v-model="dataFormEdit.cooperatorPolicy.moocSavingPolicy.moocKeepLongTerm"
                                :label="$t('labels.moocKeepLongTerm')"
                                type="number"
                                readonly
                                :color="props.color"
                                name="moocKeepLongTerm"
                                prop="moocKeepLongTerm" />
                            <hts-form-input
                                v-model="dataFormEdit.cooperatorPolicy.moocSavingPolicy.calculatedFromEvening"
                                :label="$t('labels.moocKeepLongTermCalculatedFromEvening')"
                                type="number"
                                readonly
                                :color="props.color"
                                name="calculatedFromEvening"
                                prop="calculatedFromEvening" />
                        </div>
                    </div>
                    <span class="text-base block px-0 font-medium text-default-500">
                        {{ $t('labels.holidaySurchargePolicy') }}
                    </span>
                    <div class="pl-10 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <hts-form-input
                            v-model="dataFormEdit.cooperatorPolicy.otherSurchargePolicy.towingContainerSunday"
                            :label="$t('labels.towingContainerSunday')"
                            type="number"
                            readonly
                            :color="props.color"
                            name="towingContainerSunday"
                            prop="towingContainerSunday" />
                        <hts-form-input
                            v-model="dataFormEdit.cooperatorPolicy.otherSurchargePolicy.towingContainerHoliday"
                            :label="$t('labels.towingContainerHoliday')"
                            type="number"
                            readonly
                            :color="props.color"
                            name="towingContainerHoliday"
                            prop="towingContainerHoliday" />
                    </div>
                    <span class="text-base block px-0 font-medium text-default-500">
                        {{ $t('labels.reeferPluginCharges') }}
                    </span>
                    <div class="pl-10 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <hts-form-input
                            v-model="dataFormEdit.cooperatorPolicy.reeferPluginCharges.reeferPluginChargesGenerator"
                            :label="$t('labels.reeferPluginChargesGenerator')"
                            type="number"
                            readonly
                            :color="props.color"
                            name="reeferPluginChargesGenerator"
                            prop="reeferPluginChargesGenerator" />
                    </div>
                </div>
            </hts-card>
        </div>
        <template #footer />
    </hts-drawer>
</template>

<script setup lang="ts">
import form, { IEditCooperator } from './imports/form'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  customer: {
    type: Object
  }
})

const dataFormEdit = reactive<IEditCooperator>({
  name: '',
  email: '',
  taxNumber: '',
  website: '',
  address: '',
  phone: {
    countryCode: 'Vietnam +84',
    phone: '',
    code: '+84'
  },
  representation: '',
  office: '',
  fax: '',
  type: [],
  curator: '',
  curatorName: '',
  commission: null,
  markup: null,
  isSendMail: false,
  statusMarkup: false,
  statusConfirmMarkup: null,
  locationName: '',
  location: null,
  cooperatorPolicy: {
    moocSavingPolicy: {
      freeHourNumber: null,
      moocKeepHourly: null,
      moocKeepOvernight: null,
      calculatedByHourNextDay: null,
      moocKeepLongTerm: null,
      calculatedFromEvening: null
    },
    otherSurchargePolicy: {
      towingContainerSunday: null,
      towingContainerHoliday: null
    },
    reeferPluginCharges: {
      reeferPluginChargesGenerator: null
    }
  }
})
const cooperatorId = ref('')
watch(props, () => {
  _init()
})

const _init = () => {}
_init()

const visible = ref(false)
const creating = ref(false)
const loading = ref(false)

const open = (id: string) => {
  cooperatorId.value = id
  visible.value = true
  loading.value = false
  getData()
}
defineExpose({
  open
})

const closeOverSide = () => {
  visible.value = false
}

const getData = () => {
  const api = $urlPartner.partner['get-detail']
  $api(api, { uriParams: { ID: cooperatorId.value } })
    .then((data: any) => {
      const resData: any = data?.data?.value
      if (resData?.success) {
        loading.value = true
        if (!resData.data.cooperatorPolicy) {
          delete resData.data.cooperatorPolicy
        }
        Object.assign(dataFormEdit, { ...form.initEditCustomer, ...resData.data })
      }
    })
    .catch(() => {})
}

const customerTypeTabs = computed(() => {
  const tabs: any = []
  const keyTypes: any = []
  if (dataFormEdit.type.includes('CUSTOMER')) {
    tabs.push({
      id: 'CUSTOMER',
      label: $t('labels.customer')
    })
    keyTypes.push('CUSTOMER')
  }

  if (dataFormEdit.type.includes('PARTNER')) {
    tabs.push({
      id: 'PARTNER',
      label: $t('labels.partner')
    })
    keyTypes.push('PARTNER')
  }

  if (!keyTypes.length) {
    selectCustomerTypeTabs.value = ''
  } else if (!keyTypes.includes(selectCustomerTypeTabs.value)) {
    selectCustomerTypeTabs.value = keyTypes[0]
  }

  return tabs
})

const selectCooperatorTypes = [
  // {
  //   label: $t('labels.customer'),
  //   value: 'CUSTOMER'
  // },
  {
    label: $t('labels.partner'),
    value: 'PARTNER'
  }
  // {
  //   label: $t('labels.vendor'),
  //   value: 'VENDOR'
  // }
]

const selectCustomerTypeTabs = ref('')
</script>
